import React from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Heading,
  Text,
  Icon,
  List,
  ListItem,
  ListIcon,
  Container,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FaCircle } from "react-icons/fa";

function Index() {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions</title>
        <meta name="description" content="NCVS - Terms & Conditions" />
        <meta name="keywords" content="" />
      </Helmet>

      <Box minH="100vh" color="white" py={10}>
        <Container maxW="container.lg" py={8}>
          <Box mb="5">
            <Link to="/auth/login">
              <Text fontSize={{ sm: "md", md: "lg" }} color="#000000">
                <Icon as={ArrowBackIcon} /> Back
              </Text>
            </Link>
          </Box>

          <Box
            maxW="container.lg"
            mx="auto"
            p={6}
            color="#000000"
            fontFamily={"Urbanist"}
            fontSize={{ sm: "md", md: "lg" }}
          >
            <Heading
              as="h1"
              size="xl"
              color="#000000"
              mb={6}
              textAlign="center"
              fontFamily={"Urbanist"}
            >
              Privacy Policy
            </Heading>

            <Text mb={8}>
              This Privacy Policy describes how the National Education
              Repository and Databank (NERD) collects, uses, and protects your
              information when you use the National Credential Verification
              Service (NCVS). By using our services, you consent to the data
              practices described in this policy.
            </Text>

            {/* Information We Collect */}
            <Box mb={8}>
              <Heading
                as="h2"
                size={{ sm: "md", md: "md" }}
                color="#000000"
                mb={4}
              >
                What Information Do We Collect?
              </Heading>

              <Text mb={4}>
                The National Credential Verification Service (NCVS) collects
                information necessary to provide and improve our verification
                services:
              </Text>

              <List spacing={3} mb={4}>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Account Information:
                  </Text>{" "}
                  When you create an account, we collect your name, email
                  address, institutional affiliation, and professional
                  credentials.
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Verification Data:
                  </Text>{" "}
                  We collect the academic and professional credentials you
                  submit for verification, including certificates, transcripts,
                  and supporting documents.
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Metadata:
                  </Text>{" "}
                  We automatically collect metadata about submitted documents
                  including creation dates, issuing institutions, and document
                  types.
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Usage Data:
                  </Text>{" "}
                  We collect information about how you interact with our
                  platform including verification requests made and services
                  accessed.
                </ListItem>
              </List>

              <Text fontStyle="italic">
                In accordance with the National Policy on NERD, electronic or
                digital copies of verified records are securely stored in
                perpetuity and shall remain preserved across generations with
                appropriate preservation methodologies.
              </Text>
            </Box>

            {/* Information We Don't Collect */}
            <Box mb={8}>
              <Heading
                as="h2"
                size={{ sm: "md", md: "md" }}
                color="#000000"
                mb={4}
              >
                What Information Don't We Collect?
              </Heading>

              <Text mb={4}>
                NCVS is designed with privacy as a fundamental principle. We do
                not collect:
              </Text>

              <List spacing={3} mb={4}>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  Personal browsing history, internet traffic data, or DNS
                  queries
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  Connection logs including IP addresses or session timestamps
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  Any data that could identify an individual's general internet
                  activity
                </ListItem>
              </List>

              <Text>
                We maintain minimal operational logs for system integrity,
                dispute resolution, and audit purposes only. Even when legally
                compelled, we cannot provide data we do not possess.
              </Text>
            </Box>

            {/* How We Use Information */}
            <Box mb={8}>
              <Heading
                as="h2"
                size={{ sm: "md", md: "md" }}
                color="#000000"
                mb={4}
              >
                How We Use Personal Information
              </Heading>

              <Text mb={4}>
                Your information is used solely for the purpose of providing and
                improving credential verification services:
              </Text>

              <List spacing={3} mb={4}>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Verification Services:
                  </Text>{" "}
                  To authenticate and verify academic and professional
                  credentials
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Account Management:
                  </Text>{" "}
                  To maintain and secure your account
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Service Improvement:
                  </Text>{" "}
                  To enhance our verification algorithms and user experience
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  <Text as="span" fontWeight="bold">
                    Compliance:
                  </Text>{" "}
                  To meet legal and regulatory requirements
                </ListItem>
              </List>

              <Text mb={4} fontWeight="bold">
                We do not sell your personal data to advertisers or third
                parties.
              </Text>

              <Box bg="gray.50" p={4} borderRadius="md" mb={4} color="#000000">
                <Heading as="h3" size="md" mb={2}>
                  Additional Data Uses:
                </Heading>
                <Text mb={2}>
                  • Network connections between educational institutions and
                  credential holders
                </Text>
                <Text mb={2}>• Usage patterns to improve service delivery</Text>
                <Text>
                  • Super-encrypted financial transaction data for payment
                  processing
                </Text>
              </Box>
            </Box>

            {/* Data Security */}
            <Box mb={8}>
              <Heading
                as="h2"
                size={{ sm: "md", md: "md" }}
                color="#000000"
                mb={4}
              >
                Data Security
              </Heading>

              <Text mb={4}>
                NCVS employs robust security measures to protect your
                information:
              </Text>

              <List spacing={3} mb={4}>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  End-to-end encryption for all credential data in transit and
                  at rest
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  Multi-factor authentication for all system access
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCircle} color="#000000" boxSize={2} />
                  Regular security audits and penetration testing
                </ListItem>
              </List>

              <Text>
                Our systems comply with the Nigeria Data Protection Regulation
                (NDPR) and international data protection standards.
              </Text>
            </Box>

            {/* Data Retention */}
            <Box mb={8}>
              <Heading
                as="h2"
                size={{ sm: "md", md: "md" }}
                color="#000000"
                mb={4}
              >
                Data Retention
              </Heading>

              <Text>
                Verified credentials are retained indefinitely as part of
                Nigeria's national education records. Personal account
                information is retained for 7 years after account deactivation
                or as required by law. Operational logs are retained for 2
                years.
              </Text>
            </Box>

            {/* Changes to Policy */}
            <Box>
              <Heading
                as="h2"
                size={{ sm: "md", md: "md" }}
                color="#000000"
                mb={4}
              >
                Changes to This Policy
              </Heading>

              <Text mb={4}>
                We may update this Privacy Policy periodically. The updated
                version will be indicated by an updated "Revised" date and will
                be effective immediately upon posting.
              </Text>

              <Text>
                For questions about this Privacy Policy or our data practices,
                please contact our Data Protection Officer at dpo@nerd.gov.ng.
              </Text>
            </Box>
          </Box>

          <Text textAlign="center" fontSize="sm">
            © {new Date().getFullYear()} National Education Repository and
            Databank (NERD). All rights reserved.
          </Text>
        </Container>
      </Box>
    </>
  );
}

export default Index;
